import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Download, Info, Eye } from 'lucide-react';
import PropertyCard from './ui/PropertyCard';
import Alert from '@mui/material/Alert';
import {
  Button,
  Typography,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { propertyApi } from '../../services/BaseApiService.jsx';

/**
 * Componente para visualização de certidões relacionadas à propriedade
 * Exibe uma lista de arquivos/certidões disponíveis para download
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.propertyData - Dados da propriedade
 * @param {Function} props.onHighlightGeometry - Função para destacar geometria no mapa
 * @returns {JSX.Element} Componente de visualização de certidões
 */
const CertidoesTab = ({ propertyData, _onHighlightGeometry }) => {
  // Extrai file_id do snapshot
  const rawFileId =
    propertyData?._metadata?.file_id ||
    propertyData?._contextData?.filename ||
    propertyData?._contextData?.accepted_file_id ||
    '';
  const fileId = useMemo(() => {
    if (typeof rawFileId === 'string' && rawFileId.toLowerCase().endsWith('.json'))
      return rawFileId.slice(0, -5);
    return rawFileId || '';
  }, [rawFileId]);
  // Lista real de PDFs no AFS/local
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [_error, setError] = useState(null);

  // Estado para controlar a abertura do modal de visualização
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCertidao, setSelectedCertidao] = useState(null);

  // Busca a lista de PDFs do backend
  useEffect(() => {
    let cancelled = false;
    async function fetchDocs() {
      try {
        setLoading(true);
        setError(null);
        if (!fileId) {
          setDocs([]);
          setLoading(false);
          return;
        }
        const res = await propertyApi.get(`/api/v1/docs/${encodeURIComponent(fileId)}`);
        const items = (res?.data?.files || []).map((name, idx) => ({ id: `${idx}-${name}`, name }));
        if (!cancelled) setDocs(items);
      } catch (_e) {
        if (!cancelled) {
          setError('Falha ao listar documentos');
          setDocs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchDocs();
    return () => {
      cancelled = true;
    };
  }, [fileId]);

  // Gera URL absoluta/relativa para abrir/baixar o PDF
  const buildDocUrl = (name) => {
    try {
      const base = propertyApi?.defaults?.baseURL || '/api/v1';
      return `${base}/docs/${encodeURIComponent(fileId)}/${name.split('/').map(encodeURIComponent).join('/')}`;
    } catch {
      return `/api/v1/docs/${encodeURIComponent(fileId)}/${name}`;
    }
  };

  // Ações
  const fetchDocBlob = async (name) => {
    const path = name.split('/').map(encodeURIComponent).join('/');
    const url = `/api/v1/docs/${encodeURIComponent(fileId)}/${path}`;
    const resp = await propertyApi.get(url, { responseType: 'blob' });
    // Usa o content-type retornado quando disponível; fallback application/pdf
    const contentType = resp?.headers?.['content-type'] || 'application/pdf';
    return new Blob([resp.data], { type: contentType });
  };

  const handleDownload = async (item) => {
    try {
      setLoading(true);
      const blob = await fetchDocBlob(item.name);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = item.name.split('/').pop();
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (_e) {
      setError('Falha ao baixar documento');
    } finally {
      setLoading(false);
    }
  };

  // Função para abrir o modal de visualização
  const handleView = async (certidao) => {
    try {
      setSelectedCertidao(certidao);
      setViewModalOpen(true);
      // Busca o blob com cabeçalhos e abre em nova aba
      const blob = await fetchDocBlob(certidao.name);
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      // Revoga a URL após algum tempo para liberar memória
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (_e) {
      setError('Falha ao abrir documento');
    }
  };

  // Dados a exibir: sempre reais, sem mocks
  const dadosParaExibir = docs.map((d) => ({ id: d.id, name: d.name }));

  return (
    <div className="space-y-4">
      {/* Modal para visualização de certidão */}
      <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedCertidao?.name?.split('/').pop() || 'Visualização de Documento'}
          <IconButton
            onClick={() => setViewModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedCertidao && (
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Arquivo:</strong> {selectedCertidao?.name?.split('/').pop()}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  startIcon={<Eye size={14} />}
                  onClick={() => window.open(buildDocUrl(selectedCertidao.name), '_blank')}
                  sx={{ textTransform: 'none', mr: 1 }}
                >
                  Abrir PDF
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  startIcon={<Download size={14} />}
                  onClick={() => handleDownload(selectedCertidao)}
                  sx={{ textTransform: 'none' }}
                >
                  Baixar
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <FileText className="text-purple-600 mr-2" size={20} />
          <h3 className="text-lg font-medium text-purple-800">Documentos</h3>
        </div>
      </div>

      {/* Conteúdo principal */}
      <PropertyCard subtitle="Documentos disponíveis para a propriedade" color="purple">
        {loading ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Carregando documentos...
          </Alert>
        ) : dadosParaExibir.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Não há documentos disponíveis para esta propriedade.
          </Alert>
        ) : (
          <div className="space-y-2">
            {/* Cabeçalho da lista de arquivos */}
            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-purple-50 rounded-md font-medium text-purple-800">
              <div className="col-span-8">Arquivo</div>
              <div className="col-span-4 text-right">Ação</div>
            </div>

            {/* Lista de arquivos */}
            {dadosParaExibir.map((item, index) => (
              <React.Fragment key={item.id || index}>
                <div className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-gray-50 items-center">
                  <div className="col-span-8 flex items-center">
                    <FileText size={16} className="text-purple-500 mr-2" />
                    <span className="truncate">{item.name}</span>
                  </div>
                  <div className="col-span-4 text-right flex justify-end space-x-2">
                    <Button
                      variant="outlined"
                      size="small"
                      color="secondary"
                      startIcon={<Eye size={14} />}
                      onClick={() => handleView(item)}
                      sx={{
                        textTransform: 'none',
                        borderColor: 'rgb(147, 51, 234)',
                        color: 'rgb(147, 51, 234)',
                        '&:hover': {
                          borderColor: 'rgb(126, 34, 206)',
                          backgroundColor: 'rgba(147, 51, 234, 0.04)',
                        },
                        minWidth: '80px',
                      }}
                    >
                      Ver
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="secondary"
                      startIcon={<Download size={14} />}
                      onClick={() => handleDownload(item)}
                      sx={{
                        textTransform: 'none',
                        borderColor: 'rgb(147, 51, 234)',
                        color: 'rgb(147, 51, 234)',
                        '&:hover': {
                          borderColor: 'rgb(126, 34, 206)',
                          backgroundColor: 'rgba(147, 51, 234, 0.04)',
                        },
                        minWidth: '80px',
                      }}
                    >
                      Baixar
                    </Button>
                  </div>
                </div>
                {index < dadosParaExibir.length - 1 && <Divider sx={{ my: 0.5 }} />}
              </React.Fragment>
            ))}

            {/* Informações adicionais */}
            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
              <Info size={16} className="text-purple-500 mr-2" />
              <Typography variant="body2" color="text.secondary">
                Certidões são documentos oficiais que atestam a situação legal e fiscal da
                propriedade. Os arquivos listados são provenientes do repositório configurado
                (AFS/local).
              </Typography>
            </Box>
          </div>
        )}
      </PropertyCard>
    </div>
  );
};

export default CertidoesTab;
